/**
 * Bright Ideas: makes product photos sit evenly in their boxes on menu pages
 * (collection lists, picture columns, the homepage "Shop by category" tiles).
 *
 * Supplier photos have different amounts of white space baked in, so the same
 * size box shows some appliances tiny and others huge. For each photo on a white
 * background this measures where the appliance actually is, then scales and
 * centres it so every appliance fills its box by the same amount.
 * Lifestyle photos (no plain white background) are left exactly as they are.
 */
(function () {
  var TARGETS = [
    '.collection-list .card__media img',
    '.multicolumn-card__image-wrapper--full-width img',
    '[class*="ai-page-list"] img',
    '.bi-lp-tile__media img'
  ].join(',');
  var FILL = 0.84;      // appliance fills 84% of the box's longest side
  var AREA = 0.52;      // ...but never more than about half the box's area
  var MAX_SCALE = 2.4;  // never blow a small photo up more than this
  var SAMPLE = 120;     // analyse a small copy of the image

  function sizedSrc(src) {
    // Ask Shopify's image CDN for a small copy (same site, so the canvas can read it)
    try {
      var u = new URL(src, window.location.href);
      if (u.origin !== window.location.origin && !/cdn\.shopify\.com$/.test(u.hostname)) return null;
      u.searchParams.set('width', String(SAMPLE * 2));
      return u.toString();
    } catch (e) { return null; }
  }

  function analyse(img) {
    if (img.dataset.biFit) return;
    img.dataset.biFit = 'pending';
    var src = sizedSrc(img.currentSrc || img.src);
    if (!src) { img.dataset.biFit = 'skip'; return; }
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try { apply(img, measure(probe)); } catch (e) { img.dataset.biFit = 'skip'; }
    };
    probe.onerror = function () { img.dataset.biFit = 'skip'; };
    probe.src = src;
  }

  function measure(probe) {
    var w = SAMPLE, h = Math.max(1, Math.round(SAMPLE * probe.naturalHeight / probe.naturalWidth));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h); // transparent PNGs count as white
    ctx.drawImage(probe, 0, 0, w, h);
    var d = ctx.getImageData(0, 0, w, h).data;

    function px(x, y) { var i = (y * w + x) * 4; return [d[i], d[i + 1], d[i + 2]]; }
    // Background must be near-white in all four corners, otherwise it's a photo: skip
    var corners = [px(1, 1), px(w - 2, 1), px(1, h - 2), px(w - 2, h - 2)];
    var light = corners.every(function (p) { return p[0] > 232 && p[1] > 232 && p[2] > 232; });
    if (!light) return null;

    var minX = w, minY = h, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        // "Not background" = noticeably darker than white
        if (d[i] < 236 || d[i + 1] < 236 || d[i + 2] < 236) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    return { x0: minX / w, y0: minY / h, x1: (maxX + 1) / w, y1: (maxY + 1) / h, ratio: probe.naturalWidth / probe.naturalHeight };
  }

  function apply(img, b) {
    if (!b) { img.dataset.biFit = 'skip'; return; }
    // Very wide or very tall content (brand logos, banners): leave the theme's layout alone
    var shape = ((b.x1 - b.x0) / (b.y1 - b.y0)) * b.ratio;
    if (shape > 2.2 || shape < 0.22) { img.dataset.biFit = 'skip'; return; }
    // Picture columns only become square white boxes once we know it's an appliance photo
    if (img.closest('.multicolumn-card__image-wrapper--full-width')) img.parentElement.classList.add('bi-fit-box');
    var box = img.parentElement.getBoundingClientRect();
    if (!box.width || !box.height) { img.dataset.biFit = ''; return; }
    var boxRatio = box.width / box.height;
    // Size of the picture inside the box with object-fit: contain (as fractions of the box)
    var cw = 1, ch = 1;
    if (b.ratio > boxRatio) ch = boxRatio / b.ratio; else cw = b.ratio / boxRatio;
    var left = (1 - cw) / 2 + b.x0 * cw;
    var top = (1 - ch) / 2 + b.y0 * ch;
    var pw = (b.x1 - b.x0) * cw;
    var ph = (b.y1 - b.y0) * ch;
    // Longest side ends up at FILL of the box, and squarer appliances are held back a
    // little (AREA) so a tall cooker and a wide oven look equally prominent
    var s = Math.min(FILL / pw, FILL / ph, Math.sqrt(AREA / (pw * ph)), MAX_SCALE);
    var cx = left + pw / 2;
    var cy = top + ph / 2;
    img.style.setProperty('--bi-ox', (cx * 100).toFixed(2) + '%');
    img.style.setProperty('--bi-oy', (cy * 100).toFixed(2) + '%');
    img.style.setProperty('--bi-dx', ((0.5 - cx) * 100).toFixed(2) + '%');
    img.style.setProperty('--bi-dy', ((0.5 - cy) * 100).toFixed(2) + '%');
    img.style.setProperty('--bi-s', s.toFixed(3));
    img.dataset.biFit = 'done';
  }

  function run(root) {
    (root || document).querySelectorAll(TARGETS).forEach(function (img) {
      if (img.dataset.biFit) return;
      img.classList.add('bi-fit-img');
      if (img.complete && img.naturalWidth) analyse(img);
      else img.addEventListener('load', function () { analyse(img); }, { once: true });
    });
  }

  var t;
  function boot() {
    run(document);
    new MutationObserver(function () { clearTimeout(t); t = setTimeout(run, 100); })
      .observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
